import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle } from 'lucide-react';

const TYPE_COLORS = {
  added:    'text-emerald-700 bg-emerald-50 border-emerald-200',
  removed:  'text-red-700 bg-red-50 border-red-200',
  modified: 'text-amber-700 bg-amber-50 border-amber-200',
  visual:   'text-indigo-700 bg-indigo-50 border-indigo-200',
};

const TYPE_GLYPH = { added: '+', removed: '−', modified: '~', visual: '◇' };

export default function ChangeList({
  changes,
  activeChangeId,
  onSelect,
  compareState = 'complete',
  errorMessage = null,
}) {
  const { t } = useTranslation();

  // Group by page index for cleaner rendering.
  const byPage = groupByPage(changes);

  // Error state — show the actual error, not a misleading "identical" message.
  if (compareState === 'error') {
    return (
      <div className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3 text-red-600">
          <AlertCircle size={24} />
        </div>
        <h3 className="font-semibold text-gray-900">{t('tools:compare.error_heading', 'Compare failed')}</h3>
        <p className="text-sm text-gray-500 mt-1 break-words">{errorMessage || t('tools:compare.error_generic', 'Something went wrong reading these PDFs.')}</p>
      </div>
    );
  }

  // Loading state — only show "identical" once the comparison has actually
  // run. While loading, no changes received yet doesn't mean "they match" —
  // it means "we haven't checked them yet." This was the bug behind the
  // misleading "These files are identical" screenshot on broken sessions.
  if (compareState === 'loading' && changes.length === 0) {
    return (
      <div className="p-6 text-center">
        <Loader2 size={28} className="mx-auto mb-3 text-indigo-500 animate-spin" />
        <h3 className="font-semibold text-gray-900">{t('tools:compare.comparing_heading', 'Comparing…')}</h3>
        <p className="text-sm text-gray-500 mt-1">{t('tools:compare.comparing_body', 'Extracting text and diffing pages.')}</p>
      </div>
    );
  }

  // Genuine "no changes detected" — comparison ran to completion with empty result.
  if (changes.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3 text-emerald-600 text-2xl">✓</div>
        <h3 className="font-semibold text-gray-900">{t('tools:compare.no_changes_heading', 'These files are identical')}</h3>
        <p className="text-sm text-gray-500 mt-1">{t('tools:compare.no_changes_body', 'No differences detected between the two PDFs.')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto h-full">
      {byPage.map(({ page, items }) => (
        <div key={page} className="border-b border-gray-100">
          <div className="sticky top-0 bg-gray-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            {t('tools:compare.page_label', 'Page {{n}}', { n: page + 1 })}
          </div>
          <ul>
            {items.map((c) => {
              const isActive = c.id === activeChangeId;
              return (
                <li
                  key={c.id}
                  onClick={() => onSelect(c.id)}
                  className={`px-3 py-2 text-sm cursor-pointer border-l-2 ${
                    isActive
                      ? 'bg-indigo-50 border-indigo-500'
                      : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <span className={`inline-block w-5 text-center font-mono text-xs rounded ${TYPE_COLORS[c.type] || ''} mr-2`}>
                    {TYPE_GLYPH[c.type] || '?'}
                  </span>
                  <span className="text-gray-900 truncate">{c.text || t(`tools:compare.change_${c.type}`)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

function groupByPage(changes) {
  const map = new Map();
  for (const c of changes) {
    if (!map.has(c.page)) map.set(c.page, []);
    map.get(c.page).push(c);
  }
  return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([page, items]) => ({ page, items }));
}
